/**
 * FAQ source of truth — used by both the full /faq page and the homepage preview.
 * Keep answers concise and provider/creator-friendly.
 */

export const ALL_FAQ = {
  general: [
    {
      q: "What is CheatCraft?",
      a: "CheatCraft is a marketplace that connects gaming software providers with media creators. Providers find talented creators to produce promotional videos, reviews, showcases, and tutorials for their products. Everything happens on one platform — discovery, communication, orders, and secure payments.",
    },
    {
      q: "How does CheatCraft work?",
      a: "It's simple: providers browse creator profiles, place an order, and pay securely through our escrow system. The creator produces the content, uploads it with automatic watermark protection, and the provider reviews it. Once approved, the watermark is removed and funds are released to the creator. Everyone is protected.",
    },
    {
      q: "Is CheatCraft free to use?",
      a: "Signing up and browsing is completely free for both providers and creators. CheatCraft takes a 10% service fee on each completed transaction. There are no hidden fees — you always see the full price breakdown before paying.",
    },
    {
      q: "What payment methods are accepted?",
      a: "Providers can pay using credit/debit cards. Creators can withdraw their earnings via bank transfer or cryptocurrency (BTC, ETH, USDT).",
    },
  ],
  providers: [
    {
      q: "How do I find the right creator?",
      a: "Use our filters to narrow down creators by game, video quality, language, price range, equipment (mic, facecam), and content type. You can also sort by rating, price, or subscriber count. Check their portfolio videos and reviews from other providers before ordering.",
    },
    {
      q: "How does the escrow system protect me?",
      a: "When you place an order, your payment is held securely in escrow. The creator never receives the funds until you review and approve the delivered content. If the work doesn't match your requirements, you can request a revision. Your money is safe until you're satisfied.",
    },
    {
      q: "What if I'm not happy with the delivered video?",
      a: "You can request up to 2 free revisions. Leave feedback explaining what needs to change, and the creator will update the content. If you're still not satisfied after revisions, contact our support team and we'll help resolve the dispute.",
    },
    {
      q: "Can I see the video before releasing payment?",
      a: "Yes. Every delivered video is protected with a CHEATCRAFT watermark. You can watch the full video and check quality, but the watermark prevents unauthorized use. Once you approve, the watermark is removed and you get the clean file.",
    },
    {
      q: "How long does it take to receive my video?",
      a: "Delivery time depends on the creator and complexity of the project. Most orders are delivered within 3–7 days. You set the deadline when placing your order, and some creators offer rush delivery as an add-on for faster turnaround.",
    },
    {
      q: "Can I order from multiple creators at the same time?",
      a: "Yes. You can place as many orders as you want with different creators simultaneously. Each order is tracked independently in your dashboard.",
    },
  ],
  creators: [
    {
      q: "How do I get started as a creator?",
      a: "Sign up for free, complete your profile (games, equipment, pricing, portfolio videos), and you'll appear in search results once your profile is at least 80% complete. Providers will find you and place orders directly.",
    },
    {
      q: "How do creators get paid?",
      a: "When a provider approves your delivery, the funds are released to your CheatCraft balance. You can withdraw anytime to your bank account or crypto wallet (BTC, ETH, USDT). Minimum withdrawal is $20.",
    },
    {
      q: "How long until I receive my money?",
      a: "Funds are added to your balance instantly after the provider approves. Bank withdrawals typically take 2–5 business days. Crypto withdrawals are processed within 24 hours.",
    },
    {
      q: "What are the fees?",
      a: "CheatCraft takes a 10% fee on each completed order. For example, if your video price is $100, you receive $90. The fee covers payment processing, platform maintenance, and watermark protection.",
    },
    {
      q: "What happens when I receive an order?",
      a: "You'll get an email notification and a notification on the platform. You can review the order requirements and accept or decline. Once accepted, create the content and upload it through the platform. The watermark is added automatically.",
    },
    {
      q: "Can I set my own prices?",
      a: "Absolutely. You set your base video price and can create custom add-ons (upsells) with your own pricing. For example, you could offer facecam for +$5, custom intro for +$15, or rush delivery for +$20. You're in full control.",
    },
    {
      q: "What if a provider never approves my delivery?",
      a: "If a provider doesn't respond within 7 days after delivery, the funds are automatically released to your balance. You'll never be left waiting forever.",
    },
    {
      q: "Can I decline an order?",
      a: "Yes. If an order doesn't fit your schedule or requirements, you can decline it with no penalty. We recommend responding within 24 hours so the provider can find another creator quickly.",
    },
  ],
  security: [
    {
      q: "Is my payment information safe?",
      a: "All payments are processed through Stripe, a world-leading payment processor. CheatCraft never stores your credit card details. All transactions are encrypted with bank-level security.",
    },
    {
      q: "What is the watermark protection?",
      a: "Every video delivered through CheatCraft is automatically watermarked with \"CHEATCRAFT\" across the full frame. This prevents providers from using the content without releasing payment. The watermark is removed only after approval.",
    },
    {
      q: "What happens in case of a dispute?",
      a: "If a provider and creator can't agree on a delivery, our support team reviews the case. We examine the original requirements, the delivered content, and communication history to make a fair decision. Refunds are issued if the delivery clearly doesn't match the order.",
    },
    {
      q: "How do I report a problem?",
      a: "Use the \"Report\" button available on every order page, or contact our support team through the Contact page. We typically respond within 24 hours.",
    },
  ],
};

/** Six high-impact questions surfaced on the homepage. */
export const HOMEPAGE_FAQ = [
  ALL_FAQ.general[1],     // How does CheatCraft work?
  ALL_FAQ.providers[1],   // How does the escrow system protect me?
  ALL_FAQ.security[1],    // What is the watermark protection?
  ALL_FAQ.creators[1],    // How do creators get paid?
  ALL_FAQ.creators[3],    // What are the fees?
  ALL_FAQ.providers[2],   // What if I'm not happy with the delivered video?
];
